import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { CalendarClock, Repeat, CalendarDays, AlertTriangle, Loader2 } from "lucide-react";

/** Identidad del turno: clase + día + hora. Dos turnos del mismo día son distintos. */
const clave = (t) => t.horario_id || `${t.clase_id}|${t.dia}|${t.hora_inicio || ""}`;

export const TurnosAlumno = ({ alumnoId, onCambio }) => {
  const [data, setData] = useState(null);
  const [recurrenteOpen, setRecurrenteOpen] = useState(false);
  const [puntualOpen, setPuntualOpen] = useState(false);
  const [seleccion, setSeleccion] = useState([]);
  const [puntual, setPuntual] = useState({ origen: "", destino: "" });

  const load = () => api.get(`/alumnos/${alumnoId}/turnos`).then(({ data }) => setData(data));
  useEffect(() => { setData(null); load(); /* eslint-disable-next-line */ }, [alumnoId]);

  const disponibles = data?.disponibles || [];
  const actuales = data?.actuales || [];
  const porConcretar = actuales.filter((t) => t.sin_concretar);
  const sinTurno = data?.sin_turno || [];

  const abrirRecurrente = () => {
    const posibles = new Set(disponibles.map((t) => clave(t)));
    setSeleccion(actuales.map((t) => clave(t)).filter((k) => posibles.has(k)));
    setRecurrenteOpen(true);
  };

  const alternar = (t) => {
    const k = clave(t);
    setSeleccion((s) => (s.includes(k) ? s.filter((x) => x !== k) : [...s, k]));
  };

  const guardarRecurrente = async () => {
    try {
      const horarios = seleccion
        .map((k) => disponibles.find((t) => clave(t) === k))
        .filter(Boolean)
        .map((t) => ({ horario_id: t.horario_id, clase_id: t.clase_id, dia: t.dia,
                       hora_inicio: t.hora_inicio }));
      await api.put(`/alumnos/${alumnoId}/turnos`, { horarios });
      toast.success("Turno actualizado. Las clases futuras quedan reagendadas.");
      setRecurrenteOpen(false);
      await load();
      onCambio?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const guardarPuntual = async () => {
    if (!puntual.origen || !puntual.destino) return toast.error("Elige la clase original y la nueva");
    try {
      const { data: r } = await api.post(`/alumnos/${alumnoId}/cambio-puntual`, {
        sesion_origen_id: puntual.origen, sesion_destino_id: puntual.destino,
      });
      toast.success(`Cambio puntual reservado para el ${r.fecha}`);
      setPuntualOpen(false);
      setPuntual({ origen: "", destino: "" });
      onCambio?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <Card className="rounded-2xl p-6 border-border shadow-sm" data-testid="card-turnos">
      <div className="flex items-center gap-2 mb-4">
        <CalendarClock className="h-5 w-5 text-primary" />
        <h3 className="font-display text-lg font-bold">Turno semanal</h3>
      </div>

      {!data ? (
        <Loader2 className="h-5 w-5 animate-spin text-primary" data-testid="turnos-cargando" />
      ) : actuales.length === 0 ? (
        <p className="text-sm text-muted-foreground" data-testid="turnos-vacio">
          Sin turno asignado todavía.
        </p>
      ) : (
        <div className="flex flex-wrap gap-2" data-testid="turnos-actuales">
          {actuales.map((t) => (
            <Badge key={clave(t)} variant="outline"
              className={`rounded-full ${t.sin_concretar ? "border-amber-500/50 text-amber-600" : ""}`}
              data-testid={`turno-${clave(t)}`}>
              {t.dia} {t.hora_inicio || "sin hora"}
              {t.hora_fin ? `–${t.hora_fin}` : ""} · {t.clase}
            </Badge>
          ))}
        </div>
      )}

      {(porConcretar.length > 0 || sinTurno.length > 0) && (
        <div className="mt-4 rounded-xl border border-amber-500/40 bg-amber-500/10 p-3"
          data-testid="turnos-por-concretar">
          <p className="text-xs font-semibold text-amber-700 dark:text-amber-400 flex items-center gap-1.5">
            <AlertTriangle className="h-3.5 w-3.5" /> Pendiente de concretar la hora
          </p>
          <p className="text-xs text-muted-foreground mt-1.5">
            {porConcretar.length > 0
              && `Tiene día pero no hora en: ${porConcretar.map((t) => t.dia).join(", ")}. `}
            {sinTurno.length > 0
              && `Matriculado en ${sinTurno.map((s) => s.clase).join(", ")} sin turno elegido. `}
            Entra en «Cambiar día fijo» y elige la hora exacta: no se inventa ninguna.
          </p>
        </div>
      )}

      <div className="flex flex-wrap gap-2 mt-6">
        <Button variant="outline" size="sm" className="rounded-full" onClick={abrirRecurrente}
          disabled={!data} data-testid="cambiar-turno-btn">
          <Repeat className="h-3.5 w-3.5 mr-1.5" /> Cambiar día fijo
        </Button>
        <Button variant="outline" size="sm" className="rounded-full" onClick={() => setPuntualOpen(true)}
          disabled={!data} data-testid="cambio-puntual-btn">
          <CalendarDays className="h-3.5 w-3.5 mr-1.5" /> Cambio de un día
        </Button>
      </div>

      <Dialog open={recurrenteOpen} onOpenChange={setRecurrenteOpen}>
        <DialogContent className="rounded-2xl max-h-[90vh] overflow-y-auto" data-testid="turno-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl">Cambiar día fijo</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">
            Cada día y hora es un turno distinto. El cambio reagenda también las clases futuras.
          </p>
          <div className="grid gap-2 mt-2">
            {disponibles.map((t) => {
              const activo = seleccion.includes(clave(t));
              return (
                <button key={clave(t)} onClick={() => alternar(t)} data-testid={`turno-opcion-${clave(t)}`}
                  className={`flex items-center justify-between gap-3 rounded-xl border px-4 py-3 text-sm transition-colors duration-200 ${
                    activo ? "border-primary bg-accent/50 font-semibold" : "border-border hover:bg-muted"
                  }`}>
                  <span className="min-w-0 text-left">{t.dia} · {t.hora_inicio}–{t.hora_fin} · {t.clase}</span>
                  <span className="text-xs text-muted-foreground shrink-0">{t.plazas_libres} libres</span>
                </button>
              );
            })}
            {disponibles.length === 0 && (
              <p className="text-sm text-muted-foreground" data-testid="turno-sin-opciones">
                No hay clases activas con horario en su sede.
              </p>
            )}
          </div>
          <DialogFooter>
            <Button className="rounded-full font-semibold" onClick={guardarRecurrente} data-testid="save-turno-btn">
              Guardar turno
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={puntualOpen} onOpenChange={setPuntualOpen}>
        <DialogContent className="rounded-2xl" data-testid="puntual-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl">Cambio de un solo día</DialogTitle></DialogHeader>
          <div className="grid gap-4">
            <div className="grid gap-2">
              <Label>Clase a la que no va a venir</Label>
              <Select value={puntual.origen} onValueChange={(v) => setPuntual((p) => ({ ...p, origen: v }))}>
                <SelectTrigger data-testid="puntual-origen-select"><SelectValue placeholder="Elige la clase" /></SelectTrigger>
                <SelectContent>
                  {(data?.proximas || []).map((s) => (
                    <SelectItem key={s.id} value={s.id}>{s.fecha} {s.hora_inicio} · {s.clase}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-2">
              <Label>Clase a la que asistirá</Label>
              <Select value={puntual.destino} onValueChange={(v) => setPuntual((p) => ({ ...p, destino: v }))}>
                <SelectTrigger data-testid="puntual-destino-select"><SelectValue placeholder="Elige la nueva clase" /></SelectTrigger>
                <SelectContent>
                  {(data?.alternativas || []).map((s) => (
                    <SelectItem key={s.id} value={s.id}>{s.fecha} {s.hora_inicio} · {s.clase}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button className="rounded-full font-semibold" onClick={guardarPuntual} data-testid="save-puntual-btn">
              Confirmar cambio
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};
